import classNames from 'classnames';
import React from 'react';
import Button from '../components/Button';
import ExternalPostPlatformIcon from '../components/ExternalPostPlatformIcon';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import type {
  ExternalPostPlatform,
  SlimPost,
} from '../graphql/generated/graphql';
import { PostType } from '../graphql/generated/graphql';
import clearIcon from '../icons/non-prefilled/16x16/clear.svg';
import newTabIcon from '../icons/non-prefilled/16x16/newTab.svg';
import topicIcon from '../icons/non-prefilled/16x16/topic.svg';
import { getPostIcon } from '../post/PostIcon';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import { externalDisplayName } from '../utils/post/externalPost';
import Link from '../utils/router/Link';
import postPath from '../utils/url/postPath';
import topicPath from '../utils/url/topicPath';
import NewReorderableList from './NewReorderableList';
import * as styles from './SectionItemList.module.scss';

const SectionItemList = ({
  children,
  ...props
}: Omit<React.ComponentProps<typeof NewReorderableList>, 'name'>) => {
  return (
    <NewReorderableList {...props}>
      <div className={styles.container}>{children}</div>
    </NewReorderableList>
  );
};

interface BaseSectionItemListRowProps {
  index: number;
  name: string;
  path: string;
  icon: React.ReactNode;
  onDeleteClick?: () => void;
}

const BaseSectionItemListRow = ({
  index,
  name,
  path,
  icon,
  onDeleteClick,
}: BaseSectionItemListRowProps) => {
  return (
    <NewReorderableList.Row index={index} className={styles.rowContainer}>
      {({ isDragging }, dragState) => (
        <div
          className={classNames(styles.content, {
            [styles.hasActiveDrag]: dragState != null,
          })}
        >
          <NewReorderableList.DragIndicator
            className={styles.mainContent}
            index={index}
          >
            <span className={styles.iconContainer}>{icon}</span>
            <span className={styles.name}>{name}</span>
          </NewReorderableList.DragIndicator>
          {!isDragging && (
            <span className={styles.controls}>
              <Tooltip content="Open in new tab">
                <Link newTab to={path} className={styles.link}>
                  <SVGIcon className={styles.newTabIcon}>{newTabIcon}</SVGIcon>
                </Link>
              </Tooltip>
              {onDeleteClick && (
                <Tooltip content="Remove from section">
                  <Button
                    onClick={onDeleteClick}
                    className={styles.deleteButton}
                  >
                    <SVGIcon className={styles.deleteIcon}>{clearIcon}</SVGIcon>
                  </Button>
                </Tooltip>
              )}
            </span>
          )}
        </div>
      )}
    </NewReorderableList.Row>
  );
};

type Item =
  | Pick<SlimPost, 'id' | '__typename' | 'title' | 'type' | 'linkAccess'>
  | AllTopicsTreeListTopic
  | Pick<ExternalPostPlatform, 'id' | '__typename'>;

interface SectionItemListRowProps {
  item: Item;
  index: number;
  onDeleteClick?: () => void;
}

const SectionItemListRow = ({
  item,
  index,
  onDeleteClick,
}: SectionItemListRowProps) => {
  switch (item.__typename) {
    case 'ExternalPostPlatform': {
      return (
        <BaseSectionItemListRow
          path={`/integration-posts/${item.id}`}
          icon={<ExternalPostPlatformIcon id={item.id} size={12} />}
          name={externalDisplayName(item.id)}
          index={index}
          onDeleteClick={onDeleteClick}
        />
      );
    }
    case 'SlimPost': {
      return (
        <BaseSectionItemListRow
          path={postPath(item)}
          icon={
            <SVGIcon
              className={classNames(styles.postIcon, {
                [styles.post]: item.type === PostType.Post,
                [styles.template]: item.type === PostType.Template,
              })}
              size="small"
            >
              {getPostIcon(item)}
            </SVGIcon>
          }
          name={item.title}
          index={index}
          onDeleteClick={onDeleteClick}
        />
      );
    }
    case 'Topic': {
      return (
        <BaseSectionItemListRow
          path={topicPath(item)}
          icon={
            <SVGIcon className={styles.topicIcon} size="small">
              {topicIcon}
            </SVGIcon>
          }
          name={item.name}
          index={index}
          onDeleteClick={onDeleteClick}
        />
      );
    }
    default:
      return null;
  }
};

SectionItemList.Row = SectionItemListRow;

export default SectionItemList;
